import type { ConsentChoice } from "@cmp/shared";

const COOKIE_NAME = "cmp_consent_v2";
const STORAGE_KEY = "cmp_consent_v2";
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

export interface StoredConsent {
  consent_id: string;
  choice: ConsentChoice;
  policy_version: number;
  banner_version: number;
  ts: string;
}

function encode(value: StoredConsent): string {
  return encodeURIComponent(JSON.stringify(value));
}

function decode(raw: string): StoredConsent | null {
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(raw));
    if (!parsed || typeof parsed !== "object") return null;
    const v = parsed as Partial<StoredConsent>;
    if (
      !v.consent_id ||
      !v.choice ||
      typeof v.policy_version !== "number" ||
      typeof v.ts !== "string"
    )
      return null;
    return v as StoredConsent;
  } catch {
    return null;
  }
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const target = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const p = part.trim();
    if (p.startsWith(target)) return p.slice(target.length);
  }
  return null;
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === "undefined") return;
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${value}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax${secure}`;
}

function deleteCookie(name: string): void {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
}

function readLocal(): StoredConsent | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? decode(raw) : null;
  } catch {
    return null;
  }
}

function writeLocal(encoded: string): boolean {
  try {
    window.localStorage.setItem(STORAGE_KEY, encoded);
    return true;
  } catch {
    return false;
  }
}

function deleteLocal(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Read consent from BOTH cookie and localStorage, return the one with the
 * newer `ts`. If only one has a value, heal the other by rewriting it so
 * they stay in sync. Covers:
 *   - Safari ITP clearing the cookie while localStorage persists
 *   - Third-party-blocking browsers accepting localStorage but not cookies
 *   - A past write that silently failed in one of the two layers
 */
export function readStored(): StoredConsent | null {
  const cookieRaw = readCookie(COOKIE_NAME);
  const fromCookie = cookieRaw ? decode(cookieRaw) : null;
  const fromLocal = readLocal();

  if (!fromCookie && !fromLocal) return null;
  if (fromCookie && !fromLocal) {
    // Cookie is the truth; backfill localStorage.
    writeLocal(encode(fromCookie));
    return fromCookie;
  }
  if (!fromCookie && fromLocal) {
    // localStorage is the truth; backfill cookie.
    writeCookie(COOKIE_NAME, encode(fromLocal), ONE_YEAR_SECONDS);
    return fromLocal;
  }

  // Both exist — pick the newer ts.
  const winner = fromCookie!.ts >= fromLocal!.ts ? fromCookie! : fromLocal!;
  const loser = winner === fromCookie ? fromLocal! : fromCookie!;
  if (winner.ts !== loser.ts) {
    // Out of sync — rewrite both with the winner so they stay coherent.
    const encoded = encode(winner);
    writeCookie(COOKIE_NAME, encoded, ONE_YEAR_SECONDS);
    writeLocal(encoded);
  }
  return winner;
}

export function writeStored(value: StoredConsent): void {
  const encoded = encode(value);
  // Write localStorage first: it's more reliable across browsers (no ITP,
  // no 4 KB limit, survives private-mode quirks better). If the cookie
  // write silently fails, the next readStored will still return `value`
  // from localStorage and heal the cookie.
  writeLocal(encoded);
  writeCookie(COOKIE_NAME, encoded, ONE_YEAR_SECONDS);
}

export function clearStored(): void {
  deleteCookie(COOKIE_NAME);
  deleteLocal();
}
